import React from 'react';

import { KeyBindingHint } from '../components/keybinding/key-binding-hint';

type UseKeyBindingOptions = {
  /** Example: 'Ctrl+Enter', 'Shift+S', 'Enter' */
  combo: string;
  onTrigger?: () => void | Promise<void>;
  /** Optional: override the separator used in the visual hint (default '+'). */
  separator?: string;
};

/**
 * Returns a keydown handler that fires on the given combo, and a visual hint element.
 */
export function useKeyBinding(options: UseKeyBindingOptions) {
  const { combo, onTrigger, separator } = options;
  const keys = React.useMemo(() => combo.split('+').map((k) => k.trim()), [combo]);

  const handler = React.useCallback(
    (e: React.KeyboardEvent) => {
      const set = new Set(keys.map((k) => k.toLowerCase()));
      const wantCtrl = set.has('ctrl') || set.has('control');
      const wantMeta = set.has('meta') || set.has('cmd') || set.has('command');
      const wantAlt = set.has('alt') || set.has('option');
      const wantShift = set.has('shift');

      const nonModKeys = keys.filter(
        (k) =>
          !['ctrl', 'control', 'meta', 'cmd', 'command', 'alt', 'option', 'shift'].includes(
            k.toLowerCase(),
          ),
      );

      const keyMatch =
        nonModKeys.length === 0
          ? true
          : nonModKeys.some((k) => e.key.toLowerCase() === k.toLowerCase());

      if (
        keyMatch &&
        !!wantCtrl === e.ctrlKey &&
        !!wantMeta === e.metaKey &&
        !!wantAlt === e.altKey &&
        !!wantShift === e.shiftKey
      ) {
        e.preventDefault();
        const result = onTrigger?.();
        // Avoid floating promise warnings for optional async handlers
        if (result && typeof (result as Promise<unknown>).then === 'function') {
          void (result as Promise<unknown>);
        }
      }
    },
    [keys, onTrigger],
  );

  const hint = React.useMemo(
    () => <KeyBindingHint keys={keys} separator={separator} />,
    [keys, separator],
  );

  return { handler, hint } as const;
}
